import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import data from '@/lib/claimRecordComments.json';
import { CommentForm } from '@/components/spec/CommentForm';
import { OpenQuestions } from '@/components/spec/OpenQuestions';

const STATUS = {
  applied: { label: 'Applied', cls: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' },
  open: { label: 'Open', cls: 'border-amber-500/30 bg-amber-500/10 text-amber-300' },
  declined: { label: 'Not adopted', cls: 'border-white/15 bg-white/5 text-gray-300' },
};
const FILTERS = ['all', 'applied', 'open', 'declined'];
const usDate = (iso) => { const [y, m, d] = iso.split('-'); return `${m}-${d}-${y}`; };

function CommentRow({ c }) {
  const s = STATUS[c.status];
  return (
    <li className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`comment-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="font-mono text-xs text-gray-500">{c.id}</span>
        <span className={`rounded-full border px-2.5 py-0.5 text-[11px] ${s.cls}`} data-testid={`comment-${c.id}-status`}>{c.status === 'applied' && c.version ? `Applied in Draft ${c.version}` : s.label}</span>
        <span className="text-xs text-gray-400" data-testid={`comment-${c.id}-date`}>{usDate(c.date)}</span>
      </div>
      <h3 className="text-base font-semibold text-white">{c.topic}</h3>
      <p className="text-xs text-gray-500 mb-2">{c.from}{c.from === 'Public source' ? `: ${c.kind}` : ''}</p>
      <p className="text-sm text-gray-300 leading-relaxed">{c.summary}</p>
      <p className="text-xs text-gray-400 mt-2">Where: {c.where}</p>
    </li>
  );
}

export default function ClaimRecordComments() {
  const [filter, setFilter] = useState('all');
  const list = data.comments.filter((c) => filter === 'all' || c.status === filter);
  const count = (f) => (f === 'all' ? data.comments.length : data.comments.filter((c) => c.status === f).length);
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="claim-comments-page">
      <Helmet><title>Comments on the Claim Records Proposal | Kojiru</title></Helmet>
      <Navbar />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <Link to="/specs/claim-records" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors mb-8" data-testid="comments-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Claim Records</Link>
          <div className="text-xs uppercase tracking-widest text-amber-300 mb-3">Claim Records and Purchase Intent · Draft 0.4 proposal</div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4" data-testid="comments-title">Comments and how they were handled</h1>
          <p className="text-base text-gray-400 max-w-3xl leading-relaxed mb-2">Every comment that shaped the proposal, and what happened to it. Open items carry into the next draft.</p>
          <p className="text-xs text-gray-400 max-w-3xl mb-8" data-testid="comments-policy">{data.policy} Updated {data.updated}.</p>
          <OpenQuestions items={data.openQuestions} />
          <div className="flex flex-wrap gap-2 mb-6" data-testid="comments-filters">
            {FILTERS.map((f) => (
              <button key={f} type="button" onClick={() => setFilter(f)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === f ? 'border-amber-400/60 text-white' : 'border-white/15 text-gray-400 hover:text-white'}`} data-testid={`comments-filter-${f}`}>
                {f === 'all' ? 'All' : STATUS[f].label} ({count(f)})
              </button>
            ))}
          </div>
          <ul className="space-y-3" data-testid="comments-list">
            {list.map((c) => <CommentRow key={c.id} c={c} />)}
            {!list.length && <li className="text-sm text-gray-500" data-testid="comments-empty">Nothing here yet.</li>}
          </ul>
          <CommentForm />
          <p className="mt-4 text-xs text-gray-400 flex items-center gap-2" data-testid="comments-how-to"><Mail className="w-3.5 h-3.5" /><span>Prefer email? Write to <a href="mailto:wayne@kojiru.com" className="underline hover:text-white" data-testid="comments-email">wayne@kojiru.com</a>.</span></p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
